import React, { useEffect, useMemo, useState } from 'react';
import { customSplitter, giveUniqueColoredString, groupIdsByValue, utcDateToLocalDate } from "../../../../utils/Helper";
import { capitalize, find, get, includes, isEmpty, keyBy, map, sortBy, uniq, uniqWith, indexOf } from "lodash";
import AlpSpotLoadStatus from "../../alpSpotLoads/partials/AlpSpotLoadStatus";
import { Badge } from "primereact/badge";
import SpotLoadStatus from "../../spotLoads/partials/SpotLoadStatus";
import Location from "../../../../components/Common/Display/Location";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import AlpInboundLoadStatus from "../../alpInboundLoads/partials/AlpInboundLoadStatus";
import AlpInboundLoadFcaStatus from "../../alpInboundLoads/partials/AlpInboundLoadFcaStatus";
import AlpOutboundLoadStatus from "../../alpOutboundLoads/partials/AlpOutboundLoadStatus";
import { LoadStatusFlagChip } from "./LoadStatusChip";

export function ColumnLoadNumber(props: any) {
    const { t }: any = props;
    const { alp_supplier_compound, load_number, created_at, type, load_type, in_person, reexport_type }: any = props.row;
    const normalizeLoadNumbers = (raw: any): string[] => {
        const text = String(raw || "").trim();
        if ( text === "" ) {
            return [];
        }

        let values: string[] = text
            .split("||")
            .map((part: string) => {
                const cleanPart = part.trim();
                if ( cleanPart === "" ) {
                    return "";
                }

                const [name] = cleanPart.split("___");
                return (name || cleanPart).trim();
            })
            .filter((name: string) => !isEmpty(name));

        if ( values.length === 1 && values[0].includes(",") ) {
            values = values[0]
                .split(",")
                .map((name: string) => name.trim())
                .filter((name: string) => !isEmpty(name));
        }

        return Array.from(new Set<string>(values));
    };

    const renderLoadNumber = (raw: any) => {
        const loadNumbers = normalizeLoadNumbers(raw);

        if ( loadNumbers.length <= 1 ) {
            return <strong>{ loadNumbers[0] || "" }</strong>;
        }

        return (
            <div>
                { loadNumbers.map((item: string, index: number) => (
                    <div key={ `${ item }-${ index }` }>
                        <strong>{ index + 1 }. { item }</strong>
                    </div>
                )) }
            </div>
        );
    };

    const grouped = (whole: string) => {
        const deliverable: any = [];
        if ( isEmpty(whole) ) {
            return deliverable;
        }
        map(whole.split("||"), (item: any) => {
            const tokens = item.split("___");

            deliverable.push({
                deliver_to: tokens[0],
                guid: tokens[1],
            });
        });

        return deliverable;
    };

    if ( type === "AlpSpotLoad" ) {
        const { alp_dealer_name, alp_supplier_compound, guid_colors } = props.row;
        const compounds: any = grouped(alp_supplier_compound);
        const dealers: any = grouped(alp_dealer_name);
        const colors: any = grouped(guid_colors);

        const load_types: any[] = [];
        map(load_type.split("||"), (item: any) => {
            const compound_guid = item.split("___");
            let deliver_name: string | null = null;


            if ( compound_guid[0] === "compound" ) {
                deliver_name = find(compounds, { guid: compound_guid[1] });
            }

            if ( compound_guid[0] === "dealer" ) {
                deliver_name = find(dealers, { guid: compound_guid[1] });
            }

            load_types.push({
                deliver_to: compound_guid[0],
                guid: compound_guid[1],
                deliver_name: get(deliver_name, ['deliver_to']),
                color: get(find(colors, { deliver_to: compound_guid[1] }), ['guid']),
            });
        });

        const allDeliveryTo = uniqWith(load_types, (x, y) => ( x.deliver_to === y.deliver_to && x.deliver_name === y.deliver_name ));

        return <>
            <div>
                <div>{ renderLoadNumber(load_number) }</div>
                <div>{ utcDateToLocalDate(created_at) }</div>
                <span>{ type.replace('Alp', 'EF ') }</span>
                { allDeliveryTo.map((item: any, index: number) => {
                    return <div key={ index }>
                        <div className={ "cursor-pointer" }>
                            <div>
                                <span className={ 'circle-indicator' } key={ index } style={ { color: item.color } }>
                                    <i className="text-xxs fas fa-circle"></i>
                                </span>
                                &nbsp;
                                { ( item.deliver_to === 'compound' ) &&
                                  <Tag value={ t("compound") } severity="success" className={ "text-capitalize" }
                                       style={ { fontSize: "0.5rem" } } /> }
                                { ( item.deliver_to === 'dealer' ) &&
                                  <Tag value={ t("dealer") } severity="info" className={ "text-capitalize" }
                                       style={ { fontSize: "0.5rem" } } /> }
                                { ( item.deliver_to === 'default' ) &&
                                  <Tag value={ t("default") } severity="warning" className={ "text-capitalize" }
                                       style={ { fontSize: "0.5rem" } } /> }
                                &nbsp;{ item.deliver_name }
                            </div>
                        </div>
                    </div>;
                }) }
            </div>
        </>
    }

    return <div>
        <div>{ renderLoadNumber(load_number) }</div>
        <div>{ utcDateToLocalDate(created_at) }</div>
        <span>{ type.replace('Alp', 'EF ') }</span>
        { type === "AlpInboundLoad" && <div className={"text-capitalize"}>{ load_type }</div> }
        { (type === "AlpInboundLoad" && includes(['compound-to-compound', 'single-pickup'], load_type)) && <div className={"text-capitalize"}>{ alp_supplier_compound }</div> }
        { type === "AlpReexportLoad" && String(reexport_type) === '2' && <div className={"text-capitalize"}>{ t("announcement") }</div> }
        { type === "AlpReexportLoad" && !!in_person && <div className={"text-capitalize"}>{ t("in person") }</div> }
    </div>;
}

export function ColumnRenderForDriver(props: any) {
    const { row }: any = props;
    const color = giveUniqueColoredString(row.guid_colors);
    const idsOrder = uniq(row.seq.split("||"));

    return <>
        {
            groupIdsByValue(
                sortBy(
                    customSplitter(
                        ( get(row, ['driver_name']) || "" )
                        .split("||"),
                        '___',
                        false
                    )
                    , (item: any) => indexOf(idsOrder, item.id))
            )
            .map((i: any, index: number) => {
                if ( isEmpty(i.value) ) {
                    return '';
                }

                return (
                    <div key={ index }>
                        <CircleComponent i={ i } color={ color } />
                        { i.value }
                    </div>
                );
            })
        }
    </>;
}

export function ColumnRenderForClient(props: any) {
    const { row, dropdowns }: any = props;
    const color = giveUniqueColoredString(row.guid_colors);
    const idsOrder = uniq(row.seq.split("||"));

    if ( includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad", "AlpOutboundLoadGroup", "AlpReexportLoad"], row.type) ) {
        const clients = get(( get(row, ['client_name']) || "" ).split("||"), ['0']);
        const client = get(clients.split("___"), [1]);

        return <>
            { get(find(dropdowns.clients, { id: client }), ['name']) }
        </>;
    }

    return <>
        {
            groupIdsByValue(
                sortBy(
                    customSplitter(
                        ( get(row, ['client_name']) || "" )
                        .split("||"),
                        '___',
                        false
                    )
                    , (item: any) => indexOf(idsOrder, item.id))
            )
            .map((i: any, index: number) => {
                if ( isEmpty(i.value) ) {
                    return '';
                }

                return (
                    <div key={ index }>
                        <CircleComponent i={ i } color={ color } />
                        { i.value }
                    </div>
                );
            })
        }
    </>;
};

export function ColumnRenderForBrandModel(props: any) {
    const { row, dropdowns }: any = props;
    const color = giveUniqueColoredString(row.guid_colors);
    const idsOrder = uniq(row.seq.split("||"));

    if ( includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad", "AlpOutboundLoadGroup", "AlpReexportLoad"], row.type) ) {
        return <>
            {
                groupIdsByValue(
                    sortBy(
                        customSplitter(
                            ( get(row, ['brand_model']) || "" )
                            .split("||"),
                            '___',
                            false
                        )
                        , (item: any) => indexOf(idsOrder, item.id))
                )
                .map((i: any, index: number) => {
                    const model = find(dropdowns.models, { id: i.value });
                    if ( isEmpty(i.value) || isEmpty(model) ) {
                        return '';
                    }

                    return (
                        <div key={ index }>
                            <CircleComponent i={ i } color={ color } />
                            { get(model, ['label']) }
                        </div>
                    );
                })
            }
        </>;
    }

    return <>
        {
            groupIdsByValue(
                sortBy(
                    customSplitter(
                        ( get(row, ['vin_number']) || "" )
                        .split("||"),
                        '___',
                        false
                    )
                    , (item: any) => indexOf(idsOrder, item.id))
            )
            .map((i: any, index: number) => {
                if ( isEmpty(i.value) ) {
                    return '';
                }
                const data = find(groupIdsByValue(
                    customSplitter(
                        ( get(row, ['brand_model']) || "" )
                        .split("||"),
                        '___',
                        false
                    )
                ), (item) => includes(item.ids, i.value));
                const model = find(dropdowns.models, { id: get(data, ['value']) })

                return (
                    <div key={ index }>
                        <CircleComponent i={ i } color={ row.guid_colors } />
                        { get(model, ['label']) }
                    </div>
                );
            })
        }
    </>;
}

export function ColumnRenderForTransportVehicle(props: any) {
    const { row }: any = props;

    // based on color
    const color = giveUniqueColoredString(row.guid_colors);
    const idsOrder = uniq(row.seq.split("||"));
    return <>
        {
            groupIdsByValue(
                sortBy(
                    customSplitter(
                        ( get(row, ['transport_vehicle']) || "" )
                        .split("||"),
                        '___',
                        false
                    )
                    , (item: any) => indexOf(idsOrder, item.id))
            )
            .map((i: any, index: number) => {
                if ( isEmpty(i.value) ) {
                    return '';
                }

                return (
                    <div key={ index }>
                        <CircleComponent i={ i } color={ color } />
                        { i.value.replaceAll("~~~", " | ") }
                    </div>
                );
            }) }
    </>;
}

export function formatLocationCode(item: any): string {
    if ( !item ) {
        return '';
    }

    let label = [
        item.street_line_1,
        item.street_line_2,
        item.city,
        item.zip,
        item.country ? capitalize(item.country) : '',
    ].join(', ');

    label = label.trim().replace(/^,+|,+$/g, '').trim();

    return label;
}

export function getResolvedLocationItems(row: any, dropdowns: any) {
    const idsOrder = uniq((row.seq || '').split("||"));
    const orderIndexById: Record<string, number> = {};
    map(idsOrder, (id: string, index: number) => {
        orderIndexById[id] = index;
    });

    const locationsById = keyBy(get(dropdowns, ['locations'], []), 'id');
    const raw = ( get(row, ['locations']) || "" );

    if ( isEmpty(raw) ) {
        return [];
    }

    return sortBy(
        customSplitter(raw.split("||")),
        (item: any) => get(orderIndexById, [item.id], Number.MAX_SAFE_INTEGER)
    ).map((item: any) => {
        const tokens = (item.value || '').split("~~~");
        const fromId = get(tokens, [0]);
        const toId = get(tokens, [1]);

        return {
            ...item,
            fromLocation: get(locationsById, [fromId]),
            toLocation: get(locationsById, [toId]),
        };
    });
}

export function ColumnRenderForLocation(props: any) {
    const { row, dropdowns }: any = props;
    const items = useMemo(() => getResolvedLocationItems(row, dropdowns), [row, dropdowns]);

    return <>
        {
            items.map((i: any, index: number) => {
                return (
                    <div key={ index }>
                            <span className={ 'circle-indicator' }
                                  style={ { color: getColor(row.guid_colors, i.id) } }>
                                <i className="text-xxs fas fa-circle"></i>
                            </span>
                        <Location item={ i.fromLocation } titled={ 1 } />
                        <Location item={ i.toLocation } titled={ 1 } />
                    </div>
                );
            })
        }
    </>;
}

export function ColumnRenderForFromLocation(props: any) {
    const { row, dropdowns }: any = props;
    const items = useMemo(() => getResolvedLocationItems(row, dropdowns), [row, dropdowns]);

    return <>
        {
            items.map((i: any, index: number) => (
                <div key={ index }>
                    <span className={ 'circle-indicator' }
                          style={ { color: getColor(row.guid_colors, i.id) } }>
                        <i className="text-xxs fas fa-circle"></i>
                    </span>
                    { formatLocationCode(i.fromLocation) }
                </div>
            ))
        }
    </>;
}

export function ColumnRenderForToLocation(props: any) {
    const { row, dropdowns }: any = props;
    const items = useMemo(() => getResolvedLocationItems(row, dropdowns), [row, dropdowns]);

    return <>
        {
            items.map((i: any, index: number) => (
                <div key={ index }>
                    <span className={ 'circle-indicator' }
                          style={ { color: getColor(row.guid_colors, i.id) } }>
                        <i className="text-xxs fas fa-circle"></i>
                    </span>
                    { formatLocationCode(i.toLocation) }
                </div>
            ))
        }
    </>;
}

const VIN_PREVIEW_LIMIT = 2;

function getSortedVinItems(row: any) {
    const idsOrder = uniq((row.seq || '').split("||"));

    return groupIdsByValue(
        sortBy(
            customSplitter(
                ( get(row, ['vin_number']) || "" )
                .split("||"),
                '___',
                false
            )
            , (item: any) => indexOf(idsOrder, item.id))
    ).filter((item: any) => !isEmpty(item.value));
}

function VinListItem(props: {
    item: any;
    row: any;
    tooltip: string;
    onCopy: (value: string) => void;
}) {
    const { item, row, tooltip, onCopy } = props;

    return (
        <div>
            <Button
                className={ "p-button-x-sm" }
                size={ "small" }
                link
                type={ "button" }
                tooltip={ tooltip }
                tooltipOptions={ { event: "both", position: "top" } }
                style={ { fontSize: '0.975rem' } }
                onClick={ () => onCopy(item.value) }>
                <CircleComponent i={ item } color={ row.guid_colors } />
                { item.value }
            </Button>
        </div>
    );
}

export function ColumnRenderForVin(props: any) {
    const { row, t }: any = props;
    const [tooltip, setToolTip] = useState(t('click: to copy'));
    const [expanded, setExpanded] = useState(false);
    const items = useMemo(() => getSortedVinItems(row), [row]);

    useEffect(() => {
        setExpanded(false);
    }, [row.id, row.guid, row.vin_number]);

    const handleCopy = (clipboard: string) => {
        navigator.clipboard.writeText(clipboard).then(() => {
            setToolTip(t('copied'));
            setTimeout(() => {
                setToolTip(t('click: to copy'));
            }, 5000);
        }).catch(() => {
            setToolTip(t('failed to copy text'));
        });
    };

    const showToggle = items.length > VIN_PREVIEW_LIMIT;
    const visibleItems = (expanded || !showToggle)
        ? items
        : items.slice(0, VIN_PREVIEW_LIMIT);
    const remainingCount = items.length - VIN_PREVIEW_LIMIT;

    return <>
        {
            visibleItems.map((item: any, index: number) => (
                <VinListItem
                    key={ `${ item.id }-${ index }` }
                    item={ item }
                    row={ row }
                    tooltip={ tooltip }
                    onCopy={ handleCopy }
                />
            ))
        }
        {
            showToggle && !expanded &&
            <div>
                <Button
                    className={ "p-button-x-sm" }
                    size={ "small" }
                    link
                    type={ "button" }
                    style={ { fontSize: '0.975rem' } }
                    onClick={ (event) => {
                        event.stopPropagation();
                        setExpanded(true);
                    } }>
                    +{ remainingCount } { t('more') }
                </Button>
            </div>
        }
        {
            showToggle && expanded &&
            <div>
                <Button
                    className={ "p-button-x-sm" }
                    size={ "small" }
                    link
                    type={ "button" }
                    style={ { fontSize: '0.975rem' } }
                    onClick={ (event) => {
                        event.stopPropagation();
                        setExpanded(false);
                    } }>
                    { t('show less') }
                </Button>
            </div>
        }
    </>;
}

export function ColumnRenderForQty(props: any) {
    const { row }: any = props;
    const idsOrder = uniq(row.seq.split("||"));

    return <>
        {
            groupIdsByValue(
                sortBy(
                    customSplitter(
                        ( get(row, ['vin_number']) || "" )
                        .split("||"),
                        '___',
                        false
                    )
                    , (item: any) => indexOf(idsOrder, item.id))
            ).length
        }
    </>;
}

export function ColumnRenderStatus(props: any) {
    const { t, row, onClickSpotLoadStatusUpdate }: any = props;

    if ( row.type === "AlpSpotLoad" ) {
        return (
            <div className="d-flex flex-column gap-1">
                <div>
                    <AlpSpotLoadStatus status={ row.status } chip={ true } />
                </div>
                <div>
                    <AlpInboundLoadFcaStatus status={ row.fca_status } chip={ true } />
                </div>
                {
                    (
                        !isEmpty(row.from_supplier_compound)
                        && row.status !== 'announced' && row.status !== 'rejected'
                        && isEmpty(row.gate_out_confirmed_at)
                    ) &&
                  <LoadStatusFlagChip colorClass="text-red-500" label={ t('gate out: pending') } />
                }
                {
                    row.has_import_custom
                    && <LoadStatusFlagChip colorClass="text-black-500" label={ t('custom import') } />
                }
                {
                    row.has_t1_document
                    && <LoadStatusFlagChip colorClass="text-black-500" label={ t('t1 document') } />
                }
                {
                    row.has_export_custom
                    && <LoadStatusFlagChip colorClass="text-black-500" label={ t('custom export') } />
                }
            </div>
        );
    }

    if ( row.type === "AlpOutboundLoad" ) {
        return (
            <div className="d-flex flex-column gap-1">
                <div>
                    <AlpOutboundLoadStatus status={ row.status } chip={ true } />
                </div>
                {
                    (
                        !isEmpty(row.alp_supplier_compound)
                        && row.status !== 'announced' && row.status !== 'rejected'
                        && isEmpty(row.gate_out_confirmed_at)
                    ) &&
                  <LoadStatusFlagChip colorClass="text-red-500" label={ t('gate out: pending') } />
                }
                {
                    row.has_export_custom
                    && <LoadStatusFlagChip colorClass="text-black-500" label={ t('custom export') } />
                }
            </div>
        );
    }

    if ( row.type === "AlpReexportLoad" ) {
        return (
            <div className="d-flex flex-column gap-1">
                <div>
                    <AlpOutboundLoadStatus status={ row.status } chip={ true } />
                </div>
                {
                    (
                        !isEmpty(row.alp_supplier_compound)
                        && row.status !== 'announced' && row.status !== 'rejected'
                        && isEmpty(row.gate_out_confirmed_at)
                    ) &&
                  <LoadStatusFlagChip colorClass="text-red-500" label={ t('gate out: pending') } />
                }
                { row.has_export_custom && <LoadStatusFlagChip colorClass="text-black-500" label={ t('custom export') } /> }
            </div>
        );
    }

    if ( row.type === "AlpOutboundLoadGroup" ) {
        // Grouped rows use the same statuses as outbound loads, plus a local "mixed" status.
        if ( row.status === 'mixed' ) {
            const deliveredCount = Number(row.delivered_load_count || 0);
            const inTransitCount = Number(row.in_transit_load_count || 0);
            const gateOutPendingCount = Number(row.gate_out_pending_count || 0);
            const totalCount = Number(row.total_load_count || row.qty || 0);
            return (
                <div className="d-flex flex-column gap-1">
                    <LoadStatusFlagChip colorClass="text-red-500" label={ t('multiple') } />
                    {
                        totalCount > 0 &&
                        <>
                            <LoadStatusFlagChip
                                colorClass="text-black-500"
                                label={ `${ t('delivered') }: ${ deliveredCount }/${ totalCount }` }
                            />
                            <LoadStatusFlagChip
                                colorClass="text-black-500"
                                label={ `${ t('in transit') }: ${ inTransitCount }/${ totalCount }` }
                            />
                            <LoadStatusFlagChip
                                colorClass="text-black-500"
                                label={ `${ t('gate out: pending') }: ${ gateOutPendingCount }/${ totalCount }` }
                            />
                        </>
                    }
                </div>
            );
        }

        return (
            <div className="d-flex flex-column gap-1">
                <div>
                    <AlpOutboundLoadStatus status={ row.status } chip={ true } />
                </div>
                {
                    (
                        !isEmpty(row.alp_supplier_compound)
                        && row.status !== 'announced' && row.status !== 'rejected'
                        && isEmpty(row.gate_out_confirmed_at)
                    ) &&
                    <LoadStatusFlagChip colorClass="text-red-500" label={ t('gate out: pending') } />
                }
                { row.has_export_custom && <LoadStatusFlagChip colorClass="text-black-500" label={ t('custom export') } /> }
            </div>
        );
    }

    if ( row.type === "AlpInboundLoad" ) {
        return (
            <div className="d-flex flex-column gap-1">
                <div>
                    <AlpInboundLoadStatus status={ row.status } chip={ true } />
                </div>
                {
                    includes(['announced', 'rejected', 'accepted', 'confirmed', 'updated'], row.status) &&
                    <div>
                        <AlpInboundLoadFcaStatus status={ row.fca_status } chip={ true } />
                    </div>
                }
                {
                    (
                        !isEmpty(row.from_supplier_compound)
                        && row.status !== 'announced' && row.status !== 'rejected'
                        && row.status !== 'transport-order-declined' && row.status !== 'ready-to-be-loaded'
                        && isEmpty(row.gate_out_confirmed_at)
                    ) &&
                  <LoadStatusFlagChip colorClass="text-red-500" label={ t('gate out: pending') } />
                }
                {
                    (includes(['announced', 'rejected'], row.status)
                    && row.does_m_l_approval_required && !row.does_manufacturer_approval_required)
                    && <LoadStatusFlagChip colorClass="text-black-500" label={ t('early transport order') } />
                }
                {
                    row.has_import_custom
                    && <LoadStatusFlagChip colorClass="text-black-500" label={ t('custom import') } />
                }
                {
                    row.has_t1_document
                    && <LoadStatusFlagChip colorClass="text-black-500" label={ t('t1 document') } />
                }
            </div>
        );
    }

    if ( row.type === "SpotLoad" ) {
        const color = giveUniqueColoredString(row.guid_colors);
        const idsOrder = uniq(row.seq.split("||"));

        return (
            <div className="d-flex flex-column gap-1">
                    {
                        groupIdsByValue(
                            sortBy(
                                customSplitter(
                                    ( get(row, ['item_status']) || "" )
                                    .split("||"),
                                    '___',
                                    false
                                )
                                , (item: any) => indexOf(idsOrder, item.id))
                        )
                        .map((i: any, index: number) => {
                            if ( isEmpty(i.value) ) {
                                return '';
                            }
                            return (
                                <div key={ index }>
                                    {
                                        i.value === "pending"
                                        ? <>
                                            <CircleComponent i={ i } color={ color } />
                                            <SpotLoadStatus status={ i.value } chip={ true } />
                                        </>
                                        : <span
                                            className="cursor-pointer"
                                            onClick={ (event) => {
                                                event.stopPropagation();
                                                onClickSpotLoadStatusUpdate(row);
                                            } }>
                                            <CircleComponent i={ i } color={ color } />
                                            <SpotLoadStatus status={ i.value } chip={ true } />
                                        </span>
                                    }
                                </div>
                            );
                        })
                    }
                </div>
        );
    }

    return ( <div>-</div> );
}

export function ColumnRenderInvoicedAt(props: any) {
    const { row }: any = props;

    if ( includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad", "AlpOutboundLoadGroup", "AlpReexportLoad"], row.type) ) {
        return <>{ utcDateToLocalDate(row.invoiced_at) }</>;
    }

    const color = giveUniqueColoredString(row.guid_colors);
    const idsOrder = uniq(row.seq.split("||"));

    return (
        <span>
                {
                    groupIdsByValue(
                        sortBy(
                            customSplitter(
                                ( get(row, ['invoiced_at']) || "" )
                                .split("||"),
                                '___',
                                false
                            )
                            , (item: any) => indexOf(idsOrder, item.id))
                    )
                    .map((i: any, index: number) => {
                        if ( isEmpty(i.value) ) {
                            return '';
                        }

                        return (
                            <div key={ index } className={ ( index > 0 ) ? "mt-2" : "" }>
                                <CircleComponent i={ i } color={ color } />
                                { utcDateToLocalDate(i.value) }
                            </div>
                        );
                    })
                }
            </span>
    );

    return ( <div>-</div> );
}

export function ColumnRenderForInvoiceNumber(props: any) {
    const { row }: any = props;

    if ( includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad", "AlpOutboundLoadGroup", "AlpReexportLoad"], row.type) ) {
        return row.invoice_number;
    }

    if ( row.type === "SpotLoad" ) {
        const color = giveUniqueColoredString(row.guid_colors);
        const idsOrder = uniq(row.seq.split("||"));

        return (
            <span>
                    {
                        groupIdsByValue(
                            sortBy(
                                customSplitter(
                                    ( get(row, ['invoice_number']) || "" )
                                    .split("||"),
                                    '___',
                                    false
                                )
                                , (item: any) => indexOf(idsOrder, item.id))
                        )
                        .map((i: any, index: number) => {
                            if ( isEmpty(i.value) ) {
                                return '';
                            }

                            return (
                                <div key={ index } className={ ( index > 0 ) ? "mt-2" : "" }>
                                    <CircleComponent i={ i } color={ color } />
                                    { i.value }
                                </div>
                            );
                        })
                    }
                </span>
        );
    }

    return ( <div>-</div> );
}

export function ColumnRenderInTransitAt(props: any) {
    const { row }: any = props;

    if ( includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad", "AlpOutboundLoadGroup", "AlpReexportLoad"], row.type) ) {
        return <div>{ utcDateToLocalDate(row.actual_loading_time) }</div>;
    }

    const idsOrder = uniq(row.seq.split("||"));

    return <>
        {
            sortBy(
                customSplitter(
                    ( get(row, ['in_transit_at']) || "" )
                    .split("||")
                )
                , (item: any) => indexOf(idsOrder, item.id))
            .map((i: any, index: number) => {
                if ( isEmpty(i.value) ) {
                    return '';
                }

                return (
                    <div key={ index }>
                            <span className={ 'circle-indicator' }
                                  style={ { color: getColor(row.guid_colors, i.id) } }>
                                <i className="text-xxs fas fa-circle"></i>
                            </span>
                        { utcDateToLocalDate(i.value) }
                    </div>
                );
            })
        }
    </>;
}

export function ColumnRenderDeliveredAt(props: any) {
    const { row }: any = props;

    if ( includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad", "AlpOutboundLoadGroup", "AlpReexportLoad"], row.type) ) {
        return <div>{ utcDateToLocalDate(row.actual_unloading_time) }</div>;
    }

    const idsOrder = uniq(row.seq.split("||"));

    return <>
        {
            sortBy(
                customSplitter(
                    ( get(row, ['delivered_at']) || "" )
                    .split("||")
                )
                , (item: any) => indexOf(idsOrder, item.id))
            .map((i: any, index: number) => {
                if ( isEmpty(i.value) ) {
                    return '';
                }

                return (
                    <div key={ index }>
                            <span className={ 'circle-indicator' }
                                  style={ { color: getColor(row.guid_colors, i.id) } }>
                                <i className="text-xxs fas fa-circle"></i>
                            </span>
                        { utcDateToLocalDate(i.value) }
                    </div>
                );
            })
        }
    </>;
}

export function ColumnRenderActualLoadingAt(props: any) {
    const { row }: any = props;

    if ( row.type === "SpotLoad" ) {
        return <>
            {
                customSplitter(
                    ( get(row, ['actual_loading_time']) || "" )
                    .split("||")
                )
                .map((i: any, index: number) => {
                    if ( isEmpty(i.value) ) {
                        return '';
                    }

                    return (
                        <div key={ index }>
                            <span className={ 'circle-indicator' }
                                  style={ { color: getColor(row.guid_colors, i.id) } }>
                                <i className="text-xxs fas fa-circle"></i>
                            </span>
                            { utcDateToLocalDate(i.value) }
                        </div>
                    );
                })
            }
        </>;
    }

    if ( row.type === "AlpSpotLoad" ) {
        return <div>{ utcDateToLocalDate(row.actual_loading_time) }</div>;
    }

    return <></>;
}

export function ColumnRenderActualUnloadingAt(props: any) {
    const { row }: any = props;

    if ( row.type === "SpotLoad" ) {
        return <>
            {
                customSplitter(
                    ( get(row, ['actual_unloading_time']) || "" )
                    .split("||")
                )
                .map((i: any, index: number) => {
                    if ( isEmpty(i.value) ) {
                        return '';
                    }

                    return (
                        <div key={ index }>
                            <span className={ 'circle-indicator' }
                                  style={ { color: getColor(row.guid_colors, i.id) } }>
                                <i className="text-xxs fas fa-circle"></i>
                            </span>
                            { utcDateToLocalDate(i.value) }
                        </div>
                    );
                })
            }
        </>;
    }

    if ( row.type === "AlpSpotLoad" ) {
        return <div>{ utcDateToLocalDate(row.actual_unloading_time) }</div>;
    }

    return <></>;
}

export function getColor(guid_colors: any, id: string) {
    const colors = guid_colors.split("||");

    for (let i = 0; i < colors.length; i++) {
        const tokens: string[] = colors[i].split("___");
        if ( get(tokens, [0]) === id ) {
            return get(tokens, [1]);
        }
    }

    return '';
}

export function CircleComponent(row: any) {
    const { color, i } = row;

    return (
        <>
            { uniq(i.ids).map((idItem: any, idKey: number) => {
                const spanStyle = { color: getColor(color, idItem) };
                const colorValue = getColor(color, idItem);

                // Check if colorValue is truthy
                if ( colorValue ) {
                    return (
                        <span className={ 'circle-indicator' } key={ idKey } style={ spanStyle }>
                            <i className="text-xxs fas fa-circle"></i>
                        </span>
                    );
                } else {
                    // Return null if no value is returned from getColor function
                    return null;
                }
            }) }
        </>
    );
}

export function ColumnRenderForLegalEntity(props: any) {
    const { row, dropdowns }: any = props;
    const idsOrder = uniq(row.seq.split("||"));

    if ( includes(["AlpSpotLoad", "AlpInboundLoad", "AlpOutboundLoad", "AlpOutboundLoadGroup", "AlpReexportLoad"], row.type) ) {
        const fromLocation = find(dropdowns.legalEntities, { id: get(row, ['legal_entity']) });

        return (
            <div>
                { get(fromLocation, ['label']) }
            </div>
        );
    }

    return <>
        {
            // sort by guid based on status_updated_at column
            sortBy(
                // split record by ||
                customSplitter(
                    (get(row, ['legal_entity']) || "")
                    .split("||")
                )
                , (item: any) => indexOf(idsOrder, item.id))
            .map((i: any, index: number) => {
                const locations = i.value.split("~~~");
                const fromLocation = find(dropdowns.legalEntities, {id: get(locations, [1])})

                return (
                    <div key={ index }>
                        <span className={ 'circle-indicator' }
                              style={ { color: getColor(row.guid_colors, i.id) } }>
                            <i className="text-xxs fas fa-circle"></i>
                        </span>
                        { get(fromLocation, ['label']) }
                    </div>
                )
            })
        }
    </>
}

export function legalEntityLabel(props: any) {
    const { row, dropdowns }: any = props;
    const idsOrder = uniq(row.seq.split("||"));

    return groupIdsByValue(
        sortBy(
            customSplitter(
                ( get(row, ['legal_entity']) || "" )
                .split("||"),
                '___',
                false
            )
            , (item: any) => indexOf(idsOrder, item.id))
    )
    .map((i: any, index: number) => {
        const model = find(dropdowns.legalEntities, { id: i.value });

        if ( isEmpty(i.value) || isEmpty(model) ) {
            return '';
        }

        return get(model, ['label']);
    })
    .filter((i: any) => !isEmpty(i))
    .join(',');
}
